// Server state shared across the app. Query keys live here so pages invalidate the same caches.
import { createContext, useContext } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import type { FeedsResponse, Me } from "../api/types";

export const keys = {
  me: ["me"] as const,
  feeds: ["feeds"] as const,
  searches: ["searches"] as const,
  unread: ["alerts", "count"] as const,
  brief: ["brief"] as const,
  feedItems: (id: number, view: string, q: string) => ["feed", id, "items", view, q] as const,
  notice: (key: string, feed: number | null) => ["notice", key, feed] as const,
  budget: (params: string) => ["budget", params] as const,
  budgetItem: (id: string) => ["budget", "item", id] as const,
};

export const useMe = () =>
  useQuery<Me | null>({ queryKey: keys.me, queryFn: () => api<Me>("/api/me"), retry: false, staleTime: 60_000 });

export const useFeeds = (enabled = true) =>
  useQuery({ queryKey: keys.feeds, queryFn: () => api<FeedsResponse>("/api/feeds"), enabled, staleTime: 30_000 });

export interface SavedSearch {
  id: number;
  name: string;
  query: string;
  sort: "best" | "due" | "newest";
  frequency: "off" | "daily" | "instant";
}

export const useSavedSearches = (enabled = true) =>
  useQuery({
    queryKey: keys.searches,
    queryFn: () => api<{ searches: SavedSearch[] }>("/api/searches").then((d) => d.searches),
    enabled,
    staleTime: 30_000,
  });

export const useUnread = (enabled = true) =>
  useQuery({
    queryKey: keys.unread,
    queryFn: () => api<{ unread: number }>("/api/alerts/count").then((d) => d.unread),
    enabled,
    refetchInterval: 60_000,
  });

/** After a mark, exclusion or edit: everything that lists records may have changed. */
export function useRefreshLists() {
  const client = useQueryClient();
  return () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ["feed"] }),
      client.invalidateQueries({ queryKey: keys.brief }),
      client.invalidateQueries({ queryKey: keys.feeds }),
    ]);
}

// The signed-in user, for everything under the router's signed-in branch. Read from context rather than
// the query so that signing out (the query becoming null) swaps in the sign-in screen before any page
// re-renders without a user.
const SessionContext = createContext<Me | null>(null);
export const SessionProvider = SessionContext.Provider;
export function useSession(): Me {
  const me = useContext(SessionContext);
  if (!me) throw new Error("useSession outside a signed-in page");
  return me;
}
